import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { createArticle, uploadImage, generateSlug } from '../../api';

function AdminCreate() {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    
    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        meta_title: '',
        meta_description: '',
        cover_image_url: '',
        content_html: '',
        category: '',
        tags: '',
        status: 0,
        published_at: ''
    });
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleContentChange = (content) => {
        setFormData(prev => ({ ...prev, content_html: content }));
    };

    const handleGenerateSlug = async () => {
        if (!formData.title) return;
        try {
            const res = await generateSlug(formData.title);
            setFormData(prev => ({ ...prev, slug: res.data.slug }));
        } catch (err) {
            console.error('Error generating slug:', err);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Preview
        const reader = new FileReader();
        reader.onload = (e) => setImagePreview(e.target.result);
        reader.readAsDataURL(file);

        // Upload
        try {
            const formDataUpload = new FormData();
            formDataUpload.append('image', file);
            const res = await uploadImage(formDataUpload);
            setFormData(prev => ({ ...prev, cover_image_url: res.data.url }));
        } catch (err) {
            console.error('Error uploading image:', err);
            setError('Errore nel caricamento dell\'immagine');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const dataToSend = {
                ...formData,
                status: parseInt(formData.status),
                published_at: formData.published_at || null
            };
            
            await createArticle(dataToSend);
            navigate('/admin/blog');
        } catch (err) {
            console.error('Error creating article:', err);
            setError(err.response?.data?.error || 'Errore nella creazione dell\'articolo');
        } finally {
            setLoading(false);
        }
    };

    const quillModules = {
        toolbar: [
            [{ 'header': [2, 3, false] }],
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'image'],
            ['clean']
        ]
    };

    return (
        <>
            <header className="admin-header">
                <h1>Nuovo Articolo</h1>
            </header>

            <div className="admin-content">
                {error && (
                    <div className="alert alert-error">{error}</div>
                )}

                <form onSubmit={handleSubmit} className="article-form">
                    <div className="form-grid">
                        <div className="form-main">
                            <div className="form-group">
                                <label htmlFor="title">Titolo *</label>
                                <input
                                    type="text"
                                    id="title"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    required
                                    className="form-control"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="slug">Slug URL *</label>
                                <div className="input-group">
                                    <span className="input-prefix">/blog/</span>
                                    <input
                                        type="text"
                                        id="slug"
                                        name="slug"
                                        value={formData.slug}
                                        onChange={handleChange}
                                        required
                                        className="form-control"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleGenerateSlug}
                                        className="btn btn-secondary"
                                    >
                                        Genera
                                    </button>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Contenuto</label>
                                <ReactQuill
                                    value={formData.content_html}
                                    onChange={handleContentChange}
                                    modules={quillModules}
                                    theme="snow"
                                />
                            </div>
                        </div>

                        <div className="form-sidebar">
                            <div className="sidebar-section">
                                <h3>Pubblicazione</h3>
                                
                                <div className="form-group">
                                    <label htmlFor="status">Stato</label>
                                    <select
                                        id="status"
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                        className="form-control"
                                    >
                                        <option value={0}>Bozza</option>
                                        <option value={1}>Pubblicato</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="published_at">Data Pubblicazione</label>
                                    <input
                                        type="datetime-local"
                                        id="published_at"
                                        name="published_at"
                                        value={formData.published_at}
                                        onChange={handleChange}
                                        className="form-control"
                                    />
                                </div>
                            </div>

                            <div className="sidebar-section">
                                <h3>Immagine Copertina (1200 x 800 px)</h3>
                                
                                <div className="form-group">
                                    <div className="image-preview">
                                        {imagePreview || formData.cover_image_url ? (
                                            <img
                                                src={imagePreview || `${formData.cover_image_url}`}
                                                alt="Preview"
                                            />
                                        ) : (
                                            <span className="placeholder">Nessuna immagine</span>
                                        )}
                                    </div>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        className="form-control"
                                    />
                                </div>
                            </div>

                            <div className="sidebar-section">
                                <h3>Categorizzazione</h3>
                                
                                <div className="form-group">
                                    <label htmlFor="category">Categoria</label>
                                    <input
                                        type="text"
                                        id="category"
                                        name="category"
                                        value={formData.category}
                                        onChange={handleChange}
                                        className="form-control"
                                        placeholder="es. Ricambi, Guide, News"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="tags">Tags (separati da virgola)</label>
                                    <input
                                        type="text"
                                        id="tags"
                                        name="tags"
                                        value={formData.tags}
                                        onChange={handleChange}
                                        className="form-control"
                                        placeholder="es. moto, ricambi, honda"
                                    />
                                </div>
                            </div>

                            <div className="sidebar-section">
                                <h3>SEO</h3>
                                
                                <div className="form-group">
                                    <label htmlFor="meta_title">Meta Title (max 70 caratteri)</label>
                                    <input
                                        type="text"
                                        id="meta_title"
                                        name="meta_title"
                                        value={formData.meta_title}
                                        onChange={handleChange}
                                        maxLength={70}
                                        className="form-control"
                                    />
                                    <span className="char-count">{formData.meta_title.length}/70</span>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="meta_description">Meta Description (max 160 caratteri)</label>
                                    <textarea
                                        id="meta_description"
                                        name="meta_description"
                                        value={formData.meta_description}
                                        onChange={handleChange}
                                        maxLength={160}
                                        className="form-control"
                                        rows={3}
                                    />
                                    <span className="char-count">{formData.meta_description.length}/160</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-actions">
                        <Link to="/admin/blog" className="btn btn-secondary">Annulla</Link>
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? 'Salvataggio...' : 'Salva Articolo'}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
}

export default AdminCreate;
